import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiCompass,
  FiGlobe,
  FiShield,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { ImAirplane } from "react-icons/im";

import heroImage from "../../assets/images/slider/img1.jpg";
import peopleImage from "../../assets/images/whatwedo.jpg";
import insuranceLogo from "../../assets/logos/TIB.png";
import travelLogo from "../../assets/logos/TTT.png";
import "./Home.css";

const valuePillars = [
  {
    icon: FiTrendingUp,
    title: "Long-term value",
    text: "We build resilient businesses with the discipline to grow responsibly and perform consistently.",
  },
  {
    icon: FiUsers,
    title: "Customer-led thinking",
    text: "Every company in our group is shaped around real customer needs and dependable service.",
  },
  {
    icon: FiShield,
    title: "Strong governance",
    text: "Clear oversight, responsible leadership and sound risk management guide how we operate.",
  },
  {
    icon: FiGlobe,
    title: "African ambition",
    text: "Rooted in Nigeria, we are building businesses equipped to serve a connected African market.",
  },
];

const Home = () => {
  return (
    <main className="home-page">
      <section
        className="home-hero"
        style={{ "--home-hero-image": `url(${heroImage})` }}
        aria-labelledby="home-hero-title"
      >
        <div className="home-shell home-hero__content">
          <p className="home-eyebrow home-eyebrow--light">
            Thrive Holdings Limited
          </p>
          <h1 id="home-hero-title">
            Building resilient businesses.
            <span> Creating value that lasts.</span>
          </h1>
          <p className="home-hero__lead">
            We are a Nigerian holding company growing customer-focused
            businesses across insurance, travel and related services.
          </p>
          <div className="home-actions">
            <a className="home-button home-button--primary" href="#portfolio">
              Explore our companies <FiArrowRight aria-hidden="true" />
            </a>
            <Link className="home-button home-button--ghost" to="/group">
              Discover Thrive
            </Link>
          </div>
        </div>

        <div className="home-hero__footer">
          <div className="home-shell home-hero__footer-grid">
            <div>
              <strong>2</strong>
              <span>Operating companies</span>
            </div>
            <div>
              <strong>2</strong>
              <span>Focused sectors</span>
            </div>
            <div>
              <strong>Nigeria</strong>
              <span>Rooted locally, built for growth</span>
            </div>
          </div>
        </div>
      </section>

      <section className="home-intro" id="about">
        <div className="home-shell home-intro__grid">
          <div className="home-section-heading">
            <p className="home-eyebrow">Who we are</p>
            <h2>One group. A shared standard of excellence.</h2>
          </div>
          <div className="home-intro__copy">
            <p>
              Thrive Holdings brings together businesses that make everyday
              decisions simpler, safer and more rewarding. We provide the
              strategic direction, governance and shared capabilities that help
              each company serve its customers better.
            </p>
            <Link className="home-text-link" to="/group">
              Learn about our group <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="home-portfolio" id="portfolio">
        <div className="home-shell">
          <div className="home-section-heading home-section-heading--split">
            <div>
              <p className="home-eyebrow">Our portfolio</p>
              <h2>Focused businesses. Meaningful impact.</h2>
            </div>
            <p>
              Our operating companies combine specialist expertise with the
              strength, standards and long-term perspective of the group.
            </p>
          </div>

          <div className="home-portfolio__grid">
            <article className="company-card company-card--insurance">
              <div className="company-card__topline">
                <span className="company-card__icon">
                  <FiShield aria-hidden="true" />
                </span>
                <span>Insurance &amp; risk advisory</span>
              </div>
              <div className="company-card__logo">
                <img
                  src={insuranceLogo}
                  alt="Thrive Insurance Brokers Limited"
                  loading="eager"
                  decoding="sync"
                />
              </div>
              <p>
                Helping individuals and businesses understand, manage and
                transfer risk through professional insurance intermediation and
                responsive support.
              </p>
              <a
                href="https://www.insurance.thrivenig.com"
                target="_blank"
                rel="noopener noreferrer"
                className="company-card__link"
              >
                Visit company <FiArrowRight aria-hidden="true" />
              </a>
            </article>

            <article className="company-card company-card--travel">
              <div className="company-card__topline">
                <span className="company-card__icon">
                  <ImAirplane aria-hidden="true" />
                </span>
                <span>Travel management</span>
              </div>
              <div className="company-card__logo">
                <img
                  src={travelLogo}
                  alt="Thrive Travels and Tours Limited"
                  loading="eager"
                  decoding="sync"
                />
              </div>
              <p>
                Delivering reliable, personalised travel planning for
                individuals and organisations—from flights and accommodation
                to complete itineraries.
              </p>
              <a
                href="https://www.travels.thrivenig.com"
                target="_blank"
                rel="noopener noreferrer"
                className="company-card__link"
              >
                Visit company <FiArrowRight aria-hidden="true" />
              </a>
            </article>
          </div>
        </div>
      </section>

      <section className="home-value">
        <div className="home-shell">
          <div className="home-section-heading home-section-heading--center">
            <p className="home-eyebrow">How we create value</p>
            <h2>Built on clarity, capability and trust.</h2>
            <p>
              We strengthen our companies with a practical operating model
              designed for sustainable performance.
            </p>
          </div>

          <div className="home-value__grid">
            {valuePillars.map(({ icon: Icon, title, text }, index) => (
              <article className="value-card" key={title}>
                <span className="value-card__number">0{index + 1}</span>
                <Icon className="value-card__icon" aria-hidden="true" />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-purpose">
        <div
          className="home-purpose__image"
          style={{ "--home-purpose-image": `url(${peopleImage})` }}
          role="img"
          aria-label="A diverse team collaborating around a table"
        />
        <div className="home-purpose__content">
          <p className="home-eyebrow home-eyebrow--light">Our purpose</p>
          <h2>Helping people and businesses move forward with confidence.</h2>
          <p>
            We believe strong businesses should do more than perform. They
            should solve meaningful problems, create opportunity and earn trust
            over time.
          </p>
          <ul>
            <li>
              <FiCheck aria-hidden="true" /> Professional, customer-centred
              service
            </li>
            <li>
              <FiCheck aria-hidden="true" /> Technology-enabled operations
            </li>
            <li>
              <FiCheck aria-hidden="true" /> Responsible, long-term decision
              making
            </li>
          </ul>
          <Link className="home-button home-button--light" to="/what-we-do">
            See what we do <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="home-pathways">
        <div className="home-shell">
          <div className="home-pathways__grid">
            <Link className="pathway-card" to="/board-of-directors">
              <FiBriefcase aria-hidden="true" />
              <div>
                <span>Leadership</span>
                <h3>Meet the people guiding our group</h3>
              </div>
              <FiArrowRight className="pathway-card__arrow" aria-hidden="true" />
            </Link>
            <Link className="pathway-card" to="/career">
              <FiCompass aria-hidden="true" />
              <div>
                <span>Careers</span>
                <h3>Build the future with Thrive</h3>
              </div>
              <FiArrowRight className="pathway-card__arrow" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="home-cta" id="contact">
        <div className="home-shell home-cta__inner">
          <div>
            <p className="home-eyebrow home-eyebrow--light">
              Start a conversation
            </p>
            <h2>Let’s create something valuable together.</h2>
          </div>
          <div className="home-cta__action">
            <p>
              Connect with our team about partnerships, opportunities or any
              company within the Thrive group.
            </p>
            <Link className="home-button home-button--white" to="/contact">
              Contact us <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
